// Exact arithmetic for the engine. Every quantity, minute and dollar is a ratio of two
// integers until the moment it is shown, so the same inputs give the same cent every time
// and nothing is ever float-compared.

function gcd(a: bigint, b: bigint): bigint {
  if (a < 0n) a = -a;
  if (b < 0n) b = -b;
  while (b !== 0n) [a, b] = [b, a % b];
  return a;
}

export class Rat {
  readonly n: bigint;
  readonly d: bigint;

  private constructor(n: bigint, d: bigint) {
    this.n = n;
    this.d = d;
  }

  static readonly ZERO = new Rat(0n, 1n);
  static readonly ONE = new Rat(1n, 1n);

  static of(n: bigint | number, d: bigint | number = 1n): Rat {
    let nn = BigInt(n);
    let dd = BigInt(d);
    if (dd === 0n) throw new RangeError('division by zero');
    if (dd < 0n) {
      nn = -nn;
      dd = -dd;
    }
    const g = gcd(nn, dd);
    return g === 1n || g === 0n ? new Rat(nn, dd) : new Rat(nn / g, dd / g);
  }

  /** Parse a decimal such as "12.375", "-0.5", ".25" or "1e-3". Returns null when the text is not a number. */
  static tryParse(text: string | number): Rat | null {
    const s = (typeof text === 'number' ? String(text) : text).trim().replace(/,/g, '');
    const m = /^([+-])?(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/.exec(s);
    if (!m || (m[2] === '' && (m[3] === undefined || m[3] === ''))) return null;
    const sign = m[1] === '-' ? -1n : 1n;
    const whole = m[2] ?? '';
    const frac = m[3] ?? '';
    let n = BigInt((whole + frac) || '0');
    let d = 10n ** BigInt(frac.length);
    const exp = m[4] ? parseInt(m[4], 10) : 0;
    if (exp > 0) n *= 10n ** BigInt(exp);
    if (exp < 0) d *= 10n ** BigInt(-exp);
    return Rat.of(sign * n, d);
  }

  static parse(text: string | number): Rat {
    const r = Rat.tryParse(text);
    if (!r) throw new SyntaxError(`not a number: "${text}"`);
    return r;
  }

  add(o: Rat): Rat {
    return Rat.of(this.n * o.d + o.n * this.d, this.d * o.d);
  }
  sub(o: Rat): Rat {
    return Rat.of(this.n * o.d - o.n * this.d, this.d * o.d);
  }
  mul(o: Rat): Rat {
    return Rat.of(this.n * o.n, this.d * o.d);
  }
  div(o: Rat): Rat {
    if (o.n === 0n) throw new RangeError('division by zero');
    return Rat.of(this.n * o.d, this.d * o.n);
  }
  neg(): Rat {
    return new Rat(-this.n, this.d);
  }
  abs(): Rat {
    return this.n < 0n ? this.neg() : this;
  }
  cmp(o: Rat): -1 | 0 | 1 {
    const l = this.n * o.d;
    const r = o.n * this.d;
    return l < r ? -1 : l > r ? 1 : 0;
  }
  eq(o: Rat): boolean {
    return this.n === o.n && this.d === o.d;
  }
  isZero(): boolean {
    return this.n === 0n;
  }
  sign(): -1 | 0 | 1 {
    return this.n < 0n ? -1 : this.n > 0n ? 1 : 0;
  }

  /** Largest integer not above the value. */
  floor(): bigint {
    const q = this.n / this.d;
    return this.n % this.d !== 0n && this.n < 0n ? q - 1n : q;
  }
  /** Smallest integer not below the value. */
  ceil(): bigint {
    const q = this.n / this.d;
    return this.n % this.d !== 0n && this.n > 0n ? q + 1n : q;
  }
  /** Round to the nearest integer, halves away from zero (the spreadsheet rule). */
  round(): bigint {
    const twice = this.abs().mul(Rat.of(2n)).add(Rat.ONE);
    const mag = twice.floor() / 2n;
    return this.n < 0n ? -mag : mag;
  }
  /** Integer cents, halves away from zero. Money leaves the engine only through here. */
  toCents(): number {
    return Number(this.mul(Rat.of(100n)).round());
  }

  toNumber(): number {
    return Number(this.n) / Number(this.d);
  }

  /** Fixed decimal places, halves away from zero. */
  toFixed(places: number): string {
    const scale = 10n ** BigInt(places);
    const scaled = this.mul(Rat.of(scale)).round();
    const neg = scaled < 0n;
    const digits = (neg ? -scaled : scaled).toString().padStart(places + 1, '0');
    const whole = digits.slice(0, digits.length - places);
    const frac = places > 0 ? '.' + digits.slice(digits.length - places) : '';
    return (neg ? '-' : '') + whole + frac;
  }

  /** Shortest decimal up to `maxPlaces`, trailing zeros trimmed. For showing quantities. */
  toDecimal(maxPlaces = 6): string {
    const s = this.toFixed(maxPlaces);
    return s.includes('.') ? s.replace(/\.?0+$/, '') : s;
  }

  toString(): string {
    return this.toDecimal(6);
  }
}

/** Split `totalCents` across shares so the parts add up to the total exactly (largest remainder). */
export function allocateCents(totalCents: number, shares: Rat[]): number[] {
  const sum = shares.reduce((a, s) => a.add(s), Rat.ZERO);
  if (shares.length === 0) return [];
  const total = Rat.of(BigInt(totalCents));
  const exact = shares.map((s) => (sum.isZero() ? total.div(Rat.of(BigInt(shares.length))) : total.mul(s).div(sum)));
  const floors = exact.map((e) => e.floor());
  let left = BigInt(totalCents) - floors.reduce((a, f) => a + f, 0n);
  const order = exact
    .map((e, i) => ({ i, rem: e.sub(Rat.of(floors[i]!)) }))
    .sort((a, b) => b.rem.cmp(a.rem) || a.i - b.i);
  const out = floors.map((f) => Number(f));
  for (const o of order) {
    if (left <= 0n) break;
    out[o.i]! += 1;
    left -= 1n;
  }
  return out;
}
